import { supabase } from '../config/supabase';
import { invokeEdgeFunction } from '../observability/invokeEdgeFunction';

export type TripDocumentType = 'itinerary' | 'invoice';

export interface GenerateDocumentResult {
  documentId: string;
  signedUrl: string;
  filePath: string;
}

export async function generateTripDocument(
  tripId: string,
  docType: TripDocumentType
): Promise<GenerateDocumentResult> {
  const { data, error } = await invokeEdgeFunction<GenerateDocumentResult & { error?: string }>(
    'generate-trip-document',
    { body: { tripId, docType } }
  );

  if (error) {
    throw error;
  }

  const payload = data as GenerateDocumentResult & { error?: string };
  if (payload?.error) {
    throw new Error(payload.error);
  }

  return payload;
}

export async function fetchOrgDocuments(orgId: string) {
  const { data, error } = await supabase
    .from('trip_documents')
    .select('*, trips(date, pickup_address)')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function recommendRescueVehicles(sosId: string) {
  const { data, error } = await supabase.rpc('recommend_rescue_vehicles', {
    p_sos_id: sosId,
  });

  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function fetchExpiringCredentials(withinDays = 30) {
  const today = new Date();
  const end = new Date(today);
  end.setDate(end.getDate() + withinDays);
  const endStr = end.toISOString().slice(0, 10);
  const todayStr = today.toISOString().slice(0, 10);

  const [driversRes, vehiclesRes] = await Promise.all([
    supabase
      .from('drivers')
      .select('id, name, phone, license_expiry')
      .eq('status', 'active')
      .gte('license_expiry', todayStr)
      .lte('license_expiry', endStr)
      .order('license_expiry'),
    supabase
      .from('vehicles')
      .select('id, fleet_number, model, registration_expiry')
      .eq('status', 'active')
      .not('registration_expiry', 'is', null)
      .gte('registration_expiry', todayStr)
      .lte('registration_expiry', endStr)
      .order('registration_expiry'),
  ]);

  return {
    drivers: driversRes.data ?? [],
    vehicles: vehiclesRes.data ?? [],
  };
}
